"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { useTheme } from "./theme-provider"
import { 
  Brain, 
  Activity, 
  BarChart3, 
  History, 
  Settings, 
  Zap, 
  TrendingUp,
  BookOpen,
  Heart,
  Menu,
  X
} from "lucide-react"

export default function Sidebar({ activeView, setActiveView }) {
  const { theme } = useTheme()
  const [isCollapsed, setIsCollapsed] = useState(false)

  const menuItems = [
    { id: "dashboard", icon: BarChart3, label: "Dashboard", color: "#4361ee" },
    { id: "brain", icon: Brain, label: "Brain Activity", color: "#f72585" },
    { id: "analytics", icon: TrendingUp, label: "Analytics", color: "#4cc9f0" },
    { id: "history", icon: History, label: "History", color: "#7209b7" },
    { id: "scenarios", icon: Zap, label: "Scenarios", color: "#f77f00" },
    { id: "learn", icon: BookOpen, label: "Learn", color: "#06ffa5" },
    { id: "wellness", icon: Heart, label: "Wellness", color: "#ff006e" },
    { id: "settings", icon: Settings, label: "Settings", color: "#8338ec" },
  ]

  return (
    <>
      {/* Mobile Toggle Button */}
      <motion.button
        className="sidebar-mobile-toggle"
        onClick={() => setIsCollapsed(!isCollapsed)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        {isCollapsed ? <Menu size={24} /> : <X size={24} />}
      </motion.button>

      <motion.aside
        className={`modern-sidebar ${isCollapsed ? "collapsed" : ""}`}
        initial={{ x: -300 }}
        animate={{ x: 0 }}
        transition={{ type: "spring", stiffness: 100 }}
      >
        {/* Logo Section */}
        <div className="sidebar-logo">
          <motion.div
            className="logo-icon"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          >
            <Brain size={32} />
          </motion.div>
          {!isCollapsed && (
            <motion.div
              className="logo-text"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <h2>Neuro<span className="gradient-text">Ease</span></h2>
              <p>AI Brain Analytics</p>
            </motion.div>
          )}
        </div>

        {/* Menu Items */}
        <nav className="sidebar-nav">
          {menuItems.map((item, index) => {
            const Icon = item.icon
            const isActive = activeView === item.id

            return (
              <motion.button
                key={item.id}
                className={`sidebar-item ${isActive ? "active" : ""}`}
                onClick={() => setActiveView(item.id)}
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ x: 10, scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  "--item-color": item.color,
                }}
              >
                <div className="sidebar-item-icon" style={{ color: item.color }}>
                  <Icon size={22} />
                </div>
                {!isCollapsed && (
                  <span className="sidebar-item-label">{item.label}</span>
                )}
                {isActive && (
                  <motion.div
                    className="active-indicator"
                    layoutId="activeIndicator"
                    style={{ backgroundColor: item.color }}
                  />
                )}
              </motion.button>
            )
          })}
        </nav>

        {/* Status Card */}
        {!isCollapsed && (
          <motion.div
            className="sidebar-status-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="status-icon">
              <Activity size={24} />
            </div>
            <div className="status-content">
              <h4>System Status</h4>
              <div className="status-indicator">
                <span className="status-dot active"></span>
                <span>All Systems Operational</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Collapse Toggle */}
        <motion.button
          className="sidebar-collapse-btn"
          onClick={() => setIsCollapsed(!isCollapsed)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <motion.div
            animate={{ rotate: isCollapsed ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            →
          </motion.div>
        </motion.button>
      </motion.aside>
    </>
  )
}
